"use client";

import React, { useEffect, useMemo, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { stegaClean } from "@sanity/client/stega";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";

gsap.registerPlugin(ScrollTrigger, SplitText);

export interface TextScrollFadeInProps
	extends React.HTMLAttributes<HTMLHeadingElement> {
	portableText?: any[];
}

const TextScrollFadeIn: React.FC<TextScrollFadeInProps> = ({
	children,
	portableText,
	...props
}) => {
	const wrapperRef = useRef<HTMLHeadingElement | null>(null);
	const content = useMemo(() => stegaClean(portableText), [portableText]);
	const contentKey = useMemo(
		() => (content ? JSON.stringify(content) : ""),
		[content],
	);

	useEffect(() => {
		const el = wrapperRef.current;
		if (!el) return;

		const split = new SplitText(el, {
			type: "words",
			wordsClass: "word",
		});
		const words = split.words as HTMLElement[];

		words.forEach((w) => {
			w.style.display = "inline";
		});

		const ctx = gsap.context(() => {
			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: el,
					end: "center center",
					scrub: 2,
				},
			});
			tl.from(words, {
				opacity: 0.3,
				stagger: 1.2,
				duration: 2,
				ease: "power1.inOut",
			});
		}, el);

		const refresh = () => ScrollTrigger.refresh();
		window.addEventListener("load", refresh);

		return () => {
			window.removeEventListener("load", refresh);
			ctx.revert();
			split.revert();
		};
	}, [contentKey]);

	return (
		<div {...props} ref={wrapperRef}>
			{content ? (
				<PortableText value={content} components={richTextComponent} />
			) : (
				children
			)}
		</div>
	);
};
TextScrollFadeIn.displayName = "TextScrollFadeIn";

export default TextScrollFadeIn;
